import { useState } from "react";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  type VisibilityState,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@vega-core/components/shadcn/table";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isTruthy } from "@vega-core/utils/isTruthy";

import {
  EmptyCard,
  type EmptyCardProps,
} from "../../Cards/EmptyCard/EmptyCard";
import { RowsLoading } from "../RowsLoading";
import { type GroupedHeader } from "../types";

import { TableGroupHeader } from "./TableGroupHeader";

type CoreTableProps<TData, TValue> = {
  data: TData[];
  columns: ColumnDef<TData, TValue>[];
  isLoading?: boolean;
  onRowClick?: (row: TData) => void;
  wrapperClassName?: string;
  tableClassName?: string;
  rowClassName?: string;
  cellClassName?: string;
  headerClassName?: string;
  emptyState?: EmptyCardProps;
  groupedHeaders?: GroupedHeader[];
};

export const getTableCellClasses = ({
  isFirst,
  isLast,
  isFirstRow = false,
  isLastRow = false,
  isEndOfGroup = false,
}: {
  isFirstRow?: boolean;
  isLastRow?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  isEndOfGroup?: boolean;
  isMainHeader?: boolean;
}) => {
  const getPosition = () => {
    if (isFirst) return "left";

    if (isLast) return "right";

    return "middle";
  };

  const position = getPosition();

  const baseClasses =
    "align-middle border-t border-border/primary text-center defaultTransition";

  const positionClasses = {
    left: "border-l border-r",
    middle: "border-l-0 border-r-0",
    right: "border-l-0 border-r",
  }[position];

  const firstRowClasses = isFirstRow
    ? {
        left: "rounded-tl-lg border-t",
        middle: "border-t",
        right: "",
      }[position]
    : "";

  const lastRowClasses = isLastRow
    ? {
        left: "rounded-bl-lg border-b",
        middle: "border-b",
        right: "rounded-br-lg border-b",
      }[position]
    : "";

  const endOfGroupClasses = isEndOfGroup && !isLast && "border-r-[1.5rem]";

  return cnTwMerge(
    baseClasses,
    positionClasses,
    firstRowClasses,
    lastRowClasses,
    endOfGroupClasses,
  );
};

const checkIfEndOfGroup = (
  cellIndex: number,
  groupedHeaders: GroupedHeader[],
) => {
  if (!groupedHeaders.length || cellIndex === 0) return false;

  let colCount = 1; // Start at 1 to account for the first column

  return groupedHeaders.some(({ colSpan }) => {
    colCount += colSpan;

    return colCount - 1 === cellIndex;
  });
};

export function GroupedHeaderComparisonTable<TData, TValue>({
  data,
  columns,
  isLoading,
  onRowClick,
  wrapperClassName,
  tableClassName,
  rowClassName,
  cellClassName,
  headerClassName,
  emptyState,
  groupedHeaders,
}: CoreTableProps<TData, TValue>) {
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      columnVisibility,
    },
    renderFallbackValue: "-",
  });

  function renderTableContent() {
    if (isLoading) {
      return <RowsLoading columnsNumber={columns.length} />;
    }

    if (data.length === 0 && emptyState) {
      return (
        <TableBody>
          <TableRow className="hover:bg-transparent">
            <TableCell className="align-middle" colSpan={columns.length}>
              <div className="flex items-center justify-center">
                <EmptyCard
                  {...emptyState}
                  className="border-0 rounded-none shadow-none! min-h-0 h-auto py-4"
                />
              </div>
            </TableCell>
          </TableRow>
        </TableBody>
      );
    }

    return (
      <TableBody>
        {table.getRowModel().rows.map((row, rowIndex) => {
          const isLastRow = rowIndex === table.getRowModel().rows.length - 1;

          return (
            <TableRow
              className={cnTwMerge(
                "border-spacing-0",
                {
                  "cursor-pointer group hover:bg-background/secondary defaultTransition":
                    onRowClick,
                },
                rowClassName,
              )}
              key={row.id}
              onClick={() => onRowClick && onRowClick(row.original)}
            >
              {row.getVisibleCells().map((cell, cellIndex) => {
                const isFirst = cellIndex === 0;
                const isLast = cellIndex === row.getVisibleCells().length - 1;
                const columnDef = cell.column.columnDef;

                const isEndOfGroup =
                  isTruthy(groupedHeaders) &&
                  checkIfEndOfGroup(cellIndex, groupedHeaders);

                return (
                  <TableCell
                    className={cnTwMerge(
                      getTableCellClasses({
                        isFirst,
                        isLast,
                        isFirstRow: false,
                        isLastRow,
                        isEndOfGroup,
                      }),
                      columnDef.className,
                      cellClassName,
                    )}
                    key={cell.id}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                );
              })}
            </TableRow>
          );
        })}
      </TableBody>
    );
  }

  return (
    <div
      className={cnTwMerge(
        "max-w-[100vw] overflow-scroll md:overflow-auto",
        wrapperClassName,
      )}
    >
      <Table
        className={cnTwMerge(
          "border-separate border-spacing-0",
          tableClassName,
        )}
      >
        <TableHeader>
          {isTruthy(groupedHeaders) ? (
            <TableGroupHeader groupedHeaders={groupedHeaders} />
          ) : null}

          <TableRow>
            {table.getFlatHeaders().map((header, index) => {
              const isFirst = index === 0;
              const isLast = index === table.getFlatHeaders().length - 1;
              const isEndOfGroup =
                isTruthy(groupedHeaders) &&
                checkIfEndOfGroup(index, groupedHeaders);

              return (
                <TableHead
                  className={cnTwMerge(
                    getTableCellClasses({
                      isFirstRow: true,
                      isFirst,
                      isLast,
                      isEndOfGroup,
                    }),
                    header.column.columnDef.className,
                    "bg-background/secondary text-text/secondary",
                    headerClassName,
                  )}
                  key={header.id}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        {renderTableContent()}
      </Table>
    </div>
  );
}
